/**
 * LocationPickerModal - Web implementation matching mobile's location-picker.tsx
 * Allows users to search for and select locations using Mapbox Search Box API.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { debounce } from "radash";
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
  SidePanelFooter,
} from "@/components/ui/SidePanel";
import { Icon } from "@/components/ui/Icon";
import { usePublicKeys } from "@/components/providers/PublicKeysProvider";
import { useAuth } from "@/components/providers/AuthProvider";
import type {
  GeocodingFeature,
  GeocodingResponse,
  GeocodingSuggestionResponse,
  FeatureProperties,
} from "@tinyclub/shared/models";

interface LocationPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLocationSelect: (location: GeocodingFeature, locationName?: string) => void;
  hideLocationName?: boolean;
  initialLocation?: GeocodingFeature | null;
  initialLocationName?: string;
}

export function LocationPickerModal({
  open,
  onOpenChange,
  onLocationSelect,
  hideLocationName = false,
  initialLocation,
  initialLocationName = "",
}: LocationPickerModalProps) {
  const { keys } = usePublicKeys();
  const { user } = useAuth();
  const mapboxKey = keys?.mapbox_public_key;
  const sessionToken = user?.id || "anonymous";

  const searchInputRef = useRef<HTMLInputElement>(null);

  // State
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<FeatureProperties[]>([]);
  const [nearbySuggestions, setNearbySuggestions] = useState<GeocodingFeature[]>([]);
  const [pendingLocation, setPendingLocation] = useState<GeocodingFeature | undefined>(
    initialLocation || undefined
  );
  const [pendingLocationName, setPendingLocationName] = useState(initialLocationName);
  const [currentCoords, setCurrentCoords] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locationPermission, setLocationPermission] = useState<"granted" | "denied" | "unknown">("unknown");
  const [isLoadingNearby, setIsLoadingNearby] = useState(false);

  // Reset state when modal opens and focus the search input
  useEffect(() => {
    if (open) {
      setPendingLocation(initialLocation || undefined);
      setPendingLocationName(initialLocationName);
      setQuery("");
      setSuggestions([]);
      // Focus after Radix Dialog finishes its own focus management
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
    }
  }, [open, initialLocation, initialLocationName]);

  // Calculate distance between coordinates (Haversine formula)
  function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Earth's radius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  // Fetch nearby POIs
  const fetchNearbyPOIs = useCallback(
    async (latitude?: number, longitude?: number) => {
      if (!mapboxKey) return;

      setIsLoadingNearby(true);
      try {
        const categoriesWithLimits = [
          { category: "restaurant", limit: 4 },
          { category: "coffee", limit: 4 },
          { category: "shopping", limit: 4 },
          { category: "park", limit: 2 },
        ];
        const proximity =
          latitude !== undefined && longitude !== undefined
            ? `${longitude},${latitude}`
            : "ip";

        const promises = categoriesWithLimits.map(async ({ category, limit }) => {
          try {
            const url = `https://api.mapbox.com/search/searchbox/v1/category/${category}?proximity=${proximity}&limit=${limit}&access_token=${mapboxKey}`;
            const res = await fetch(url);
            if (res.ok) {
              const data = (await res.json()) as GeocodingResponse;
              return data.features || [];
            }
            return [];
          } catch {
            return [];
          }
        });

        const results = await Promise.all(promises);
        const allFeatures = results.flat();

        // Sort by distance if we have coordinates
        if (latitude !== undefined && longitude !== undefined) {
          allFeatures.sort((a, b) => {
            const distA = calculateDistance(
              latitude,
              longitude,
              a.geometry.coordinates[1],
              a.geometry.coordinates[0]
            );
            const distB = calculateDistance(
              latitude,
              longitude,
              b.geometry.coordinates[1],
              b.geometry.coordinates[0]
            );
            return distA - distB;
          });
        }

        setNearbySuggestions(allFeatures.slice(0, 10));
      } catch (err) {
        console.error("Error fetching nearby POIs:", err);
      } finally {
        setIsLoadingNearby(false);
      }
    },
    [mapboxKey]
  );

  // Fetch current location and nearby POIs on mount
  useEffect(() => {
    if (!open || !mapboxKey) return;

    async function fetchLocation() {
      if (!navigator.geolocation) {
        setLocationPermission("denied");
        await fetchNearbyPOIs();
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          setCurrentCoords(coords);
          setLocationPermission("granted");
          await fetchNearbyPOIs(coords.latitude, coords.longitude);
        },
        async () => {
          setLocationPermission("denied");
          await fetchNearbyPOIs();
        }
      );
    }

    fetchLocation();
  }, [open, mapboxKey, fetchNearbyPOIs]);

  // Search function using Mapbox Search Box API
  const doSearch = useCallback(
    async (text: string) => {
      if (!text || !mapboxKey) {
        setSuggestions([]);
        return;
      }

      try {
        const types = "poi,postcode,place,address,street,neighborhood";
        const proximity = currentCoords
          ? `${currentCoords.longitude},${currentCoords.latitude}`
          : "ip";
        const url = `https://api.mapbox.com/search/searchbox/v1/suggest?q=${encodeURIComponent(
          text
        )}&access_token=${mapboxKey}&session_token=${sessionToken}&limit=8&proximity=${proximity}&types=${types}`;

        const res = await fetch(url);
        if (!res.ok) {
          console.error("Mapbox suggest fetch failed:", res.status);
          return;
        }
        const data = (await res.json()) as GeocodingSuggestionResponse;
        setSuggestions(data.suggestions);
      } catch (err) {
        console.error("Error searching Mapbox:", err);
      }
    },
    [mapboxKey, currentCoords, sessionToken]
  );

  // Debounced search - matching mobile's radash usage
  const debouncedSearch = useMemo(
    () => debounce({ delay: 500 }, (text: string) => doSearch(text)),
    [doSearch]
  );

  useEffect(() => {
    debouncedSearch(query);
  }, [query, debouncedSearch]);

  // Retrieve full location details
  async function retrieveLocation(locationId: string): Promise<GeocodingFeature | undefined> {
    if (!mapboxKey) return;

    try {
      const url = `https://api.mapbox.com/search/searchbox/v1/retrieve/${locationId}?access_token=${mapboxKey}&session_token=${sessionToken}`;
      const res = await fetch(url);
      if (!res.ok) return;
      const data = (await res.json()) as GeocodingResponse;
      return data.features[0];
    } catch (err) {
      console.error("Error retrieving location:", err);
    }
  }

  // Reverse geocode coordinates
  async function reverseLookup(latitude: number, longitude: number): Promise<GeocodingFeature | undefined> {
    if (!mapboxKey) return;

    try {
      const url = `https://api.mapbox.com/search/searchbox/v1/reverse?longitude=${longitude}&latitude=${latitude}&access_token=${mapboxKey}`;
      const res = await fetch(url);
      if (!res.ok) return;
      const data = (await res.json()) as GeocodingResponse;
      return data.features[0];
    } catch (err) {
      console.error("Error in reverse lookup:", err);
    }
  }

  // Handle selecting a search suggestion
  async function handleSelectSuggestion(feature: FeatureProperties) {
    const fullFeature = await retrieveLocation(feature.mapbox_id);
    if (!fullFeature) return;
    setPendingLocation(fullFeature);
    setSuggestions([]);
    setQuery("");
  }

  // Handle selecting a nearby POI
  function handleSelectNearbyPOI(feature: GeocodingFeature) {
    setPendingLocation(feature);
    setQuery("");
  }

  // Handle selecting current location
  async function handleSelectCurrentLocation() {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const fullFeature = await reverseLookup(latitude, longitude);
        if (fullFeature) {
          setPendingLocation(fullFeature);
        }
      },
      (error) => {
        console.error("Error getting current location:", error);
      }
    );
  }

  // Handle Done button
  const handleDone = useCallback(() => {
    if (!pendingLocation) return;
    onLocationSelect(pendingLocation, pendingLocationName || undefined);
    onOpenChange(false);
  }, [pendingLocation, pendingLocationName, onLocationSelect, onOpenChange]);

  // Get display name from suggestion
  function getSuggestionName(feature: FeatureProperties): { name: string; address: string | undefined } {
    return {
      name: feature.name_preferred || feature.name,
      address: feature.full_address || feature.place_formatted,
    };
  }

  // Get display name from full feature
  function getFullDisplayName(feature: GeocodingFeature | undefined): { name?: string; address?: string } {
    if (!feature) return {};
    const name = feature.properties.name_preferred || feature.properties.name;
    const address = feature.properties.full_address || feature.properties.place_formatted;
    return {
      ...(name ? { name } : {}),
      ...(address ? { address } : {}),
    };
  }

  const isSearching = query.trim().length > 0;
  const selectionDisplayName = getFullDisplayName(pendingLocation);

  // Keyboard navigation for both search results and nearby places
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Reset highlight when the visible list changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [isSearching, suggestions.length, nearbySuggestions.length]);

  function handleSearchInputKeyDown(e: React.KeyboardEvent) {
    const items = isSearching ? suggestions : nearbySuggestions;
    if (items.length === 0) return;

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const next = highlightedIndex < items.length - 1 ? highlightedIndex + 1 : 0;
        setHighlightedIndex(next);
        document
          .querySelector(`[data-nav-index="${next}"]`)
          ?.scrollIntoView({ block: "nearest" });
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prev = highlightedIndex > 0 ? highlightedIndex - 1 : items.length - 1;
        setHighlightedIndex(prev);
        document
          .querySelector(`[data-nav-index="${prev}"]`)
          ?.scrollIntoView({ block: "nearest" });
        break;
      }
      case "Enter": {
        if (highlightedIndex >= 0 && highlightedIndex < items.length) {
          e.preventDefault();
          if (isSearching) {
            handleSelectSuggestion(suggestions[highlightedIndex]);
          } else {
            handleSelectNearbyPOI(nearbySuggestions[highlightedIndex]);
          }
        }
        break;
      }
      case "Escape": {
        setHighlightedIndex(-1);
        break;
      }
    }
  }

  return (
    <SidePanel open={open} onOpenChange={onOpenChange}>
      <SidePanelContent>
        <SidePanelHeader title="Location" onClose={() => onOpenChange(false)} />

        {/* Search Input - fixed at top */}
        <div className="flex-shrink-0 px-5 pb-4">
          <div className="flex items-center gap-3 border border-border-tertiary rounded-full px-4 py-3">
            <Icon name="search" size={16} className="text-text-secondary flex-shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              className="flex-1 bg-transparent text-body-1 text-text-primary placeholder:text-text-secondary outline-none"
              placeholder="Search locations"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleSearchInputKeyDown}
            />
            {query.length > 0 && (
              <button
                onClick={() => setQuery("")}
                className="text-text-tertiary hover:text-text-secondary"
              >
                <Icon name="circle-xmark" size={18} />
              </button>
            )}
          </div>
        </div>

        <SidePanelBody className="pt-0">
          {isSearching ? (
            /* Search Results */
            <div>
              {suggestions.map((item, index) => {
                const { name, address } = getSuggestionName(item);
                return (
                  <button
                    key={item.mapbox_id}
                    data-nav-index={index}
                    className={`w-full flex items-start gap-3 px-4 py-4 hover:bg-surface-1 transition-colors text-left border-b border-border-tertiary${index === highlightedIndex ? " bg-surface-1" : ""}`}
                    onClick={() => handleSelectSuggestion(item)}
                  >
                    <Icon name="map-pin" size={18} className="text-text-secondary mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      {name && (
                        <p className="text-body-1-medium text-text-primary truncate">{name}</p>
                      )}
                      {address && (
                        <p className="text-body-2 text-text-secondary truncate">{address}</p>
                      )}
                    </div>
                  </button>
                );
              })}
              {suggestions.length === 0 && query.length > 2 && (
                <p className="px-4 py-8 text-center text-text-secondary text-body-2">
                  No locations found
                </p>
              )}
            </div>
          ) : (
            /* Selected Location & Nearby Places */
            <div>
              {/* Selected Location or Current Location Button */}
              {(pendingLocation || locationPermission !== "denied") && (
                <div className="bg-surface-1 rounded-2xl p-4 flex items-start gap-3">
                  <Icon
                    name={pendingLocation ? "map-pin" : "location-arrow"}
                    size={20}
                    className="text-text-secondary mt-0.5 flex-shrink-0"
                  />

                  {pendingLocation ? (
                    <div className="flex-1 min-w-0 flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        {selectionDisplayName.name && (
                          <p className="text-body-1-medium text-text-primary">
                            {selectionDisplayName.name}
                          </p>
                        )}
                        {selectionDisplayName.address && (
                          <p className="text-body-2 text-text-secondary mt-1">
                            {selectionDisplayName.address}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => setPendingLocation(undefined)}
                        className="p-1 text-text-tertiary hover:text-text-secondary"
                      >
                        <Icon name="circle-xmark" size={18} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleSelectCurrentLocation}
                      className="flex-1 text-left text-body-1 text-text-primary hover:text-text-secondary"
                    >
                      Use current location
                    </button>
                  )}
                </div>
              )}

              {/* Location Name Input */}
              {!hideLocationName && (
                <div className="mt-6 mb-4">
                  <label className="block text-h6 text-text-secondary mb-2">
                    Location name
                  </label>
                  <input
                    type="text"
                    className="w-full bg-transparent border border-border-tertiary rounded-xl px-4 py-3 text-body-1 text-text-primary placeholder:text-text-tertiary outline-none focus:border-border-secondary"
                    placeholder="Example: Your house"
                    value={pendingLocationName}
                    onChange={(e) => setPendingLocationName(e.target.value)}
                  />
                  <p className="text-body-2 text-text-tertiary mt-2">
                    Adding a custom location name is optional.
                  </p>
                </div>
              )}

              {/* Nearby Places */}
              {nearbySuggestions.length > 0 && (
                <div className="mt-6 mb-4">
                  <h3 className="text-h6 text-text-secondary mb-2">Nearby places</h3>
                  <div>
                    {nearbySuggestions.map((poi, index) => {
                      const { name, address } = getFullDisplayName(poi);
                      return (
                        <button
                          key={poi.properties.mapbox_id}
                          data-nav-index={index}
                          className={`w-full flex items-start gap-3 px-4 py-4 hover:bg-surface-1 transition-colors text-left border-b border-border-tertiary${index === highlightedIndex ? " bg-surface-1" : ""}`}
                          onClick={() => handleSelectNearbyPOI(poi)}
                        >
                          <Icon name="map-pin" size={18} className="text-text-secondary mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            {name && (
                              <p className="text-body-1-medium text-text-primary truncate">{name}</p>
                            )}
                            {address && (
                              <p className="text-body-2 text-text-secondary truncate">{address}</p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {isLoadingNearby && nearbySuggestions.length === 0 && (
                <p className="text-center text-text-tertiary text-body-2 py-4">
                  Loading nearby places...
                </p>
              )}
            </div>
          )}
        </SidePanelBody>

        {/* Footer with Done button */}
        <SidePanelFooter>
          <button
            onClick={handleDone}
            disabled={!pendingLocation}
            className="w-full bg-olive text-white rounded-full py-3 px-6 text-body-1-medium disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
          >
            Done
          </button>
        </SidePanelFooter>
      </SidePanelContent>
    </SidePanel>
  );
}
